import { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Eye, ExternalLink, TrendingUp, Loader2, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const secureFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = { ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetch(url, { ...options, headers });
};

export default function AnalyticsPanel() {
  const [trend, setTrend] = useState([]);
  const [packageStats, setPackageStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [sortField, setSortField] = useState(null); // 'views_total', 'redirects_total', 'conversion_rate'
  const [sortDir, setSortDir] = useState('desc'); // 'asc' or 'desc'

  useEffect(() => {
    loadAnalytics();
  }, [days]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [trendRes, pkgRes] = await Promise.all([
        secureFetch(`${API_URL}/api/admin/analytics/trend?days=${days}`),
        secureFetch(`${API_URL}/api/admin/analytics/packages?days=${days}`)
      ]);
      if (trendRes.ok) setTrend(await trendRes.json());
      if (pkgRes.ok) setPackageStats(await pkgRes.json());
    } catch (e) {
      console.error('Analytics load error:', e);
    }
    setLoading(false);
  };

  const totalViews = packageStats.reduce((s, p) => s + p.views_total, 0);
  const totalRedirects = packageStats.reduce((s, p) => s + p.redirects_total, 0);
  const avgConversion = totalViews > 0 ? (totalRedirects / totalViews * 100).toFixed(1) : 0;

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return sortDir === 'desc' 
      ? <ArrowDown className="w-3 h-3 ml-1 text-amber-500" /> 
      : <ArrowUp className="w-3 h-3 ml-1 text-amber-500" />;
  };

  const sortedStats = useMemo(() => {
    const data = [...packageStats];
    if (sortField) {
      data.sort((a, b) => {
        const valA = a[sortField] || 0;
        const valB = b[sortField] || 0;
        return sortDir === 'desc' ? valB - valA : valA - valB;
      });
    }
    return data;
  }, [packageStats, sortField, sortDir]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      {/* Period selector */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-slate-700">Perioadă:</span>
        {[7, 14, 30, 90].map(d => (
          <button key={d} onClick={() => setDays(d)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              days === d
                ? 'bg-amber-500 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            data-testid={`period-${d}`}>
            {d} zile
          </button>
        ))}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-xl p-4 bg-white border border-slate-200">
          <div className="flex items-center gap-2 mb-1">
            <Eye className="w-4 h-4 text-blue-500" />
            <span className="text-xs text-slate-500">Total vizualizări</span>
          </div>
          <div className="text-2xl font-bold text-slate-800">{totalViews}</div>
        </div>
        <div className="rounded-xl p-4 bg-white border border-slate-200">
          <div className="flex items-center gap-2 mb-1">
            <ExternalLink className="w-4 h-4 text-green-500" />
            <span className="text-xs text-slate-500">Total redirecționări</span>
          </div>
          <div className="text-2xl font-bold text-slate-800">{totalRedirects}</div>
        </div>
        <div className="rounded-xl p-4 bg-white border border-slate-200">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-amber-500" />
            <span className="text-xs text-slate-500">Rată conversie</span>
          </div>
          <div className="text-2xl font-bold text-slate-800">{avgConversion}%</div>
        </div>
      </div>

      {/* Trend Chart */}
      {trend.length > 0 && (
        <div className="rounded-xl p-4 bg-white border border-slate-200">
          <h3 className="font-bold text-sm mb-4 text-slate-700">
            Evoluție ultimele {days} zile
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 11 }} />
              <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
              <Tooltip
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#334155' }}
              />
              <Legend />
              <Line type="monotone" dataKey="views" name="Vizualizări" stroke="#3b82f6" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="redirects" name="Redirecționări" stroke="#10b981" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Per-package table */}
      {packageStats.length > 0 && (
        <div className="rounded-xl overflow-hidden bg-white border border-slate-200">
          <div className="px-4 py-3 border-b border-slate-200">
            <h3 className="font-bold text-sm text-slate-700">
              Performanță per pachet (top {Math.min(packageStats.length, 20)})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-xs text-slate-500 border-b border-slate-100">
                  <th className="text-left py-2.5 px-4 font-medium">Pachet</th>
                  <th className="text-center py-2.5 px-3 font-medium">
                    <button onClick={() => toggleSort('views_total')} className="inline-flex items-center hover:text-slate-800 transition-colors" data-testid="sort-views">
                      Vizualizări <SortIcon field="views_total" />
                    </button>
                  </th>
                  <th className="text-center py-2.5 px-3 font-medium">
                    <button onClick={() => toggleSort('redirects_total')} className="inline-flex items-center hover:text-slate-800 transition-colors" data-testid="sort-redirects">
                      Redirecționări <SortIcon field="redirects_total" />
                    </button>
                  </th>
                  <th className="text-center py-2.5 px-3 font-medium">
                    <button onClick={() => toggleSort('conversion_rate')} className="inline-flex items-center hover:text-slate-800 transition-colors" data-testid="sort-conversion">
                      Conversie <SortIcon field="conversion_rate" />
                    </button>
                  </th>
                  <th className="text-right py-2.5 px-4 font-medium">Grafic</th>
                </tr>
              </thead>
              <tbody>
                {sortedStats.slice(0, 20).map(p => {
                  const maxVal = Math.max(...sortedStats.slice(0, 20).map(x => x.views_total + x.redirects_total), 1);
                  return (
                    <tr key={p.id} className="border-t border-slate-50 hover:bg-slate-50 transition-colors"
                      data-testid={`analytics-row-${p.id}`}>
                      <td className="py-2.5 px-4">
                        <div className="flex items-center gap-2">
                          {p.image_url && <img src={p.image_url} alt="" className="w-8 h-6 rounded object-cover flex-shrink-0" onError={e => { e.target.style.display = 'none'; }} />}
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-slate-800 truncate max-w-[220px]">{p.title}</div>
                            <div className="text-xs text-slate-400">{p.category} {p.price && `· ${p.price}`}</div>
                          </div>
                        </div>
                      </td>
                      <td className="text-center py-2.5 px-3">
                        <span className="text-sm font-bold text-blue-600">{p.views_total}</span>
                        {p.views_period > 0 && <span className="text-xs text-slate-400 ml-1">(+{p.views_period})</span>}
                      </td>
                      <td className="text-center py-2.5 px-3">
                        <span className="text-sm font-bold text-green-600">{p.redirects_total}</span>
                        {p.redirects_period > 0 && <span className="text-xs text-slate-400 ml-1">(+{p.redirects_period})</span>}
                      </td>
                      <td className="text-center py-2.5 px-3">
                        <span className={`text-sm font-medium ${
                          p.conversion_rate > 20 ? 'text-green-600' :
                          p.conversion_rate > 5 ? 'text-amber-600' : 'text-slate-400'
                        }`}>
                          {p.conversion_rate}%
                        </span>
                      </td>
                      <td className="py-2.5 px-4">
                        <div className="flex justify-end">
                          <div className="w-24 h-4 rounded-full overflow-hidden bg-slate-100">
                            <div className="h-full flex">
                              <div className="bg-blue-500 h-full" style={{ width: `${p.views_total / Math.max(maxVal, 1) * 100}%` }} />
                              <div className="bg-green-500 h-full" style={{ width: `${p.redirects_total / Math.max(maxVal, 1) * 100}%` }} />
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
